import {useEffect, useRef, useState} from 'react';
import type {SocialPost} from '@Components/social-media/post.vo';
import {isInvalidStepCombination, getNextWizardStep} from '@Libraries/social-media-wizard-step-library';
import {SocialMediaWizardStep} from '@Components/social-media-wizard/social-media-wizard.types';
import {
  disableNextButton,
  enableNextButton,
  initPublishingParams,
  syncPublishingParams,
  updateAccountIdsToPublishTo,
  updateAccountTypeToConnect,
  updateDesignsToPublish,
  updateEmphasizedPlatform,
  updateHasSeenConfetti,
  updateHasSeenOnboarding,
  updateHasUserPublishedBefore,
  updateIsGoogleBusinessProfileEnabled,
  updateIsSocialMediaWizardAiCaptionsEnabled,
  updateIsTemplateSelected,
  updateNumParentAccountsConnected,
  updatePost,
  updatePublishingAction,
  updatePublishingParams,
  updateWizardStep,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {useAppDispatch} from '@/hooks';
import useAutoCaptionGlobal from '@Hooks/auto-captions/useAutoCaptionGlobal';
import {addClickOutsideListener} from '@Utils/dom.util';
import useHasSeenAiCaptionIntroPopup from '@Hooks/auto-captions/useHasSeenAiCaptionIntroPopup';
import {isUserPremium} from '@Libraries/user.library';
import {requestResetAiCaptionInsights} from '@Libraries/auto-caption-library';
import type {DesignMetaUpdateInfo} from '@Components/social-media-wizard/social-media-wizard.types';
import type {DesignSelection, DesignsMetaForPublishing, SocialPlatform} from '@Components/social-media/social-media.types';
import {getCaptionErrorAccountId, getTitleErrorAccountId} from '@Libraries/social-media-params.library';
import {areAllDesignPagesSupportedForAccounts, doesVideoExist} from '@Libraries/social-media-resize.library';
import {
  hideAccountLevelErrorMessage,
  resetAccountResizeStatusForDesignsMap,
  syncEditedDesign,
  updateAccountLevelErrorMessage,
  updateAccountNeedsResizeMap,
  updateDesignsGraphicMetaMap,
  updateTriggerNext,
  updateTriggerPrevious,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {buildGraphicsMetaMap, getGraphicMeta, getSanitizedEmphasizedPlatform, isSingleDesignSelected} from '@Libraries/social-media-wizard-design-library';
import type {GraphicMeta, GraphicsMetaMapType, PageGraphicsMetaMap, PosterHashedId} from '@Components/social-media/publish-params.types';
import {MultiPostDesignActionPurposes, SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import {openErrorModal} from '@Modals/error-modal';
import {asyncnoop} from '@Utils/general.util';
import type {AjaxSuccessResponse} from '@Utils/ajax.util';
import {openDesignInEmbeddedEditor} from '@Libraries/embedded-editor';
import {getPostersPageIds, pollForPosterPreviewGenerationStatus} from '@Libraries/poster-library';
import type {SocialPostsDesignActionModalProps} from '@Components/modals/social-post-design-action-modal';
import {openDesignActionModal} from '@Components/modals/social-post-design-action-modal';
import {savePreferredProfileSelectionUser} from '@Components/social-media/social-media-thunk';
import {useAccountIdsToPublishTo, useSocialPostPublishingAction, useWizardStep} from '@Components/social-media/social-media.hooks';
import {useWizardCaptionErrors, useWizardConnectedAccounts, useWizardTitleErrors} from '@Components/social-media-wizard/social-media-wizard.hooks';
import type {SocialAccount} from '@Components/social-media/account.vo';

interface PopStateEventWithStep extends PopStateEvent {
  state: {
    step: SocialMediaWizardStep;
  };
}

interface UseWizardNavigationHistoryOpts {
  wizardStep: SocialMediaWizardStep;
  post: SocialPost | null;
}

interface UseWizardNavigationHistoryResult {
  previousSteps: SocialMediaWizardStep[];
  pushStep: () => void;
  updateWindowHistoryState: (step: SocialMediaWizardStep) => void;
  popPreviousStep: () => SocialMediaWizardStep | undefined;
}

export function useWizardNavigationHistory({wizardStep, post}: UseWizardNavigationHistoryOpts): UseWizardNavigationHistoryResult {
  const dispatch = useAppDispatch();
  const [previousSteps, setPreviousSteps] = useState<SocialMediaWizardStep[]>([]);
  const [isNavigatingBack, setIsNavigatingBack] = useState(false);
  const prevStepRef = useRef<SocialMediaWizardStep | null>(wizardStep);

  const pushStep = (): void => {
    if (wizardStep && prevStepRef.current && !isInvalidStepCombination(prevStepRef.current, wizardStep, post)) {
      setPreviousSteps([...previousSteps, prevStepRef.current]);
    }

    prevStepRef.current = wizardStep;
  };

  const popPreviousStep = (): SocialMediaWizardStep | undefined => {
    const previousStep = previousSteps[previousSteps.length - 1];

    if (previousStep !== undefined) {
      setPreviousSteps(previousSteps.slice(0, -1));
      prevStepRef.current = null;
    }

    return previousStep;
  };

  const updateWindowHistoryState = (step: SocialMediaWizardStep): void => {
    if (!isNavigatingBack) {
      window.history.pushState({step}, '', '');
    }
  };

  useEffect((): VoidFunction => {
    const onPopStateChange = (popStateEvent: PopStateEventWithStep): void => {
      if (typeof popStateEvent.state === 'object') {
        const {step} = popStateEvent.state;

        if (step) {
          dispatch(updateWizardStep(step));
          setIsNavigatingBack(true);
        }
      }
    };

    window.addEventListener('popstate', onPopStateChange);

    return (): void => {
      window.removeEventListener('popstate', onPopStateChange);
    };
  }, [dispatch]);

  return {
    previousSteps,
    pushStep,
    updateWindowHistoryState,
    popPreviousStep,
  };
}

interface UseAiCaptionWizardSetupOpts {
  connectedAccountIds: number[];
  postId: string | null;
}

export function useAiCaptionWizardSetup({connectedAccountIds, postId}: UseAiCaptionWizardSetupOpts): void {
  const {setInAutoCaptionMode} = useAutoCaptionGlobal();
  const {fetchHasSeenStatus} = useHasSeenAiCaptionIntroPopup();
  const isAiCaptionInsightsResetDone = useRef<boolean>(false);

  useEffect(() => {
    if (!isUserPremium()) {
      return;
    }

    void fetchHasSeenStatus();
  }, []);

  useEffect(() => {
    const removeListener = addClickOutsideListener(`.social-caption-container.-in-ai-mode`, onOutsideAiCaptionPromptBoxClick);

    return (): void => {
      removeListener();
    };
  }, []);

  useEffect(() => {
    if (!isUserPremium() || !connectedAccountIds.length || isAiCaptionInsightsResetDone.current || !postId) {
      return;
    }

    void requestResetAiCaptionInsights(postId, connectedAccountIds);
    isAiCaptionInsightsResetDone.current = true;
  }, [connectedAccountIds, postId]);

  const onOutsideAiCaptionPromptBoxClick = (): void => {
    setInAutoCaptionMode({accountId: null});
  };
}

const POLLING_REQUEST_TIMEOUT = 600000;

interface UseDesignMetaSyncOpts {
  designsToPublishTo: DesignSelection[];
  postId: string | undefined;
  accountIdsToPublishTo: number[];
}

interface UseDesignMetaSyncResult {
  updatePublishingDesignsMeta: (freshDesign?: DesignSelection) => Promise<DesignMetaUpdateInfo>;
  updateDesignsMetaOnClosingEmbeddedEditor: () => Promise<void>;
  fetchGraphicsMetaForAnonymousTemplates: (designToFetch: DesignSelection) => Promise<void>;
  checkUpdateInGeneratedPreviewsStatus: (designs: DesignSelection[]) => Promise<boolean>;
}

export function useDesignMetaSync({designsToPublishTo, postId, accountIdsToPublishTo}: UseDesignMetaSyncOpts): UseDesignMetaSyncResult {
  const dispatch = useAppDispatch();

  const updatePublishingDesignsMeta = async (freshDesign?: DesignSelection): Promise<DesignMetaUpdateInfo> => {
    if (designsToPublishTo.length === 0) {
      return {hasAnyVideo: false, areAllAccountsSupported: true};
    }

    const designsToUpdate = freshDesign ? [freshDesign] : designsToPublishTo;

    const data = (await window.PMW.writeLocal('socialmedia/updateDesignSelectionMetaForPublishing', {
      designSelections: JSON.stringify(designsToUpdate),
      postId: postId ?? null,
    })) as DesignsMetaForPublishing;

    const designMetaUpdateInfo: DesignMetaUpdateInfo = {
      hasAnyVideo: doesVideoExist(data.designsIsVideo),
      areAllAccountsSupported: true,
    };

    dispatch(updatePost(data.post));

    if (data.accountNeedsResizeMap) {
      const resizeMap = data.accountNeedsResizeMap;
      designMetaUpdateInfo.areAllAccountsSupported = areAllDesignPagesSupportedForAccounts(resizeMap, accountIdsToPublishTo);
      dispatch(updateAccountNeedsResizeMap(resizeMap));
    }

    dispatch(updateDesignsGraphicMetaMap(buildGraphicsMetaMap(data.previewUrls, data.designsIsVideo, data.designNames, designsToUpdate)));

    return designMetaUpdateInfo;
  };

  const updateDesignsMetaOnClosingEmbeddedEditor = async (): Promise<void> => {
    const data = (await window.PMW.writeLocal('socialmedia/updateDesignSelectionMetaForPublishing', {
      designSelections: JSON.stringify(designsToPublishTo),
      postId: postId ?? null,
    })) as DesignsMetaForPublishing;

    dispatch(updatePost(data.post));

    if (data.accountNeedsResizeMap) {
      dispatch(updateAccountNeedsResizeMap(data.accountNeedsResizeMap));
    }

    dispatch(updateDesignsGraphicMetaMap(buildGraphicsMetaMap(data.previewUrls, data.designsIsVideo, data.designNames, designsToPublishTo)));
  };

  const fetchGraphicsMetaForAnonymousTemplates = async (designToFetch: DesignSelection): Promise<void> => {
    const graphicMeta = await getGraphicMeta(designToFetch);

    const graphicsMetaMap: GraphicsMetaMapType = {};
    graphicsMetaMap[designToFetch.posterHashedId] ??= {};
    graphicsMetaMap[designToFetch.posterHashedId][designToFetch.pageHashedId] = graphicMeta;

    dispatch(updateDesignsGraphicMetaMap(graphicsMetaMap));
  };

  const checkUpdateInGeneratedPreviewsStatus = async (designs: DesignSelection[]): Promise<boolean> => {
    const pollingHaveAllUpdatedPreviewsBeenGenerated = async (): Promise<void> => {
      return (await window.PMW.writeLocal('poster/haveAllUpdatedPreviewsBeenGenerated', {designs: JSON.stringify(designs)})) as Promise<void>;
    };

    try {
      const allGenerated = (await window.PMW.pollingAjaxCallAsync(asyncnoop, pollingHaveAllUpdatedPreviewsBeenGenerated, POLLING_REQUEST_TIMEOUT)) as AjaxSuccessResponse;

      if (allGenerated) {
        return true;
      }

      openErrorModal({message: window.i18next.t('pmwsjs_unable_to_fetch_poster_previews')});
    } catch {
      openErrorModal({message: window.i18next.t('pmwsjs_unable_to_fetch_posters')});
    }

    return false;
  };

  return {
    updatePublishingDesignsMeta,
    updateDesignsMetaOnClosingEmbeddedEditor,
    fetchGraphicsMetaForAnonymousTemplates,
    checkUpdateInGeneratedPreviewsStatus,
  };
}

interface UseEmbeddedEditorFlowOpts {
  designsToPublishTo: DesignSelection[];
  designsGraphicsMetaMap: GraphicsMetaMapType;
  design: DesignSelection | undefined;
  updatePublishingDesignsMeta: (freshDesign?: DesignSelection) => Promise<DesignMetaUpdateInfo>;
  updateDesignsMetaOnClosingEmbeddedEditor: () => Promise<void>;
  setIsPreviewLoading: (loading: boolean) => void;
  setIsLoadingEmbeddedEditor: (loading: boolean) => void;
}

interface UseEmbeddedEditorFlowResult {
  onClickEditDesign: () => void;
  onEmbeddedEditorOpened: () => void;
  onEmbeddedEditorForTemplateClosed: (designIdEdited: PosterHashedId) => Promise<void>;
  triggerEditDesignsFlow: () => void;
}

export function useEmbeddedEditorFlow({
  designsToPublishTo,
  designsGraphicsMetaMap,
  design,
  updatePublishingDesignsMeta,
  updateDesignsMetaOnClosingEmbeddedEditor,
  setIsPreviewLoading,
  setIsLoadingEmbeddedEditor,
}: UseEmbeddedEditorFlowOpts): UseEmbeddedEditorFlowResult {
  const dispatch = useAppDispatch();

  const resetResizeStatesOnClosingEmbeddedEditor = (): void => {
    dispatch(resetAccountResizeStatusForDesignsMap());
  };

  const onEmbeddedEditorOpened = (): void => {
    setIsLoadingEmbeddedEditor(false);
  };

  const onEmbeddedEditorClosed = async (posterId: PosterHashedId): Promise<void> => {
    await updatePublishingDesignsMeta(design);

    const updatedEntries = Object.entries(designsGraphicsMetaMap[posterId]).map(([pageHashId, graphicMeta]) => {
      const newGraphicMeta: GraphicMeta = {
        ...graphicMeta,
        graphicURL: '',
        isVideo: false,
      };

      return [pageHashId, newGraphicMeta];
    });

    const loadingGraphicsPageMetas = Object.fromEntries(updatedEntries) as PageGraphicsMetaMap;

    const newGraphicsMetaMap: GraphicsMetaMapType = {
      ...designsGraphicsMetaMap,
      [posterId]: loadingGraphicsPageMetas,
    };

    dispatch(updateDesignsGraphicMetaMap(newGraphicsMetaMap));

    setIsPreviewLoading(true);

    const onPreviewGenerationSuccess = (): void => {
      resetResizeStatesOnClosingEmbeddedEditor();
      void updateDesignsMetaOnClosingEmbeddedEditor().finally(() => {
        setIsPreviewLoading(false);
      });
    };

    const onPreviewGenerationFailure = (err: unknown): void => {
      console.error(err);
      setIsPreviewLoading(false);
    };

    pollForPosterPreviewGenerationStatus(posterId, onPreviewGenerationSuccess, onPreviewGenerationFailure);
  };

  const onEditDesignSelectionCallback = (designToEdit: DesignSelection): void => {
    setIsLoadingEmbeddedEditor(true);

    openDesignInEmbeddedEditor(designToEdit.posterHashedId, onEmbeddedEditorOpened, () => {
      void onEmbeddedEditorClosed(designToEdit.posterHashedId);
    });
  };

  const triggerEditDesignsFlow = (): void => {
    if (isSingleDesignSelected(designsToPublishTo)) {
      onEditDesignSelectionCallback(designsToPublishTo[0]);
      return;
    }

    const designActionModal: SocialPostsDesignActionModalProps = {
      designsToPublish: designsToPublishTo,
      designsGraphicsMetaMap,
      designActionCallback: onEditDesignSelectionCallback,
      actionPurpose: MultiPostDesignActionPurposes.EDIT,
      showRemoveDesignCta: true,
      removeDesignCallback: (designs: DesignSelection[]): void => {
        setIsPreviewLoading(true);
        dispatch(updateDesignsToPublish(designs));
      },
    };

    openDesignActionModal(designActionModal);
  };

  const onClickEditDesign = (): void => {
    triggerEditDesignsFlow();
  };

  const onEmbeddedEditorForTemplateClosed = async (designIdEdited: PosterHashedId): Promise<void> => {
    const pageIdsMap = await getPostersPageIds([designIdEdited]);
    const pageId = pageIdsMap[designIdEdited].at(0);

    if (!pageId) {
      return;
    }

    const designEdited: DesignSelection = {posterHashedId: designIdEdited, pageHashedId: pageIdsMap[designIdEdited][0]};

    if (designEdited.posterHashedId !== designsToPublishTo[0].posterHashedId || designEdited.pageHashedId !== designsToPublishTo[0].pageHashedId) {
      dispatch(syncEditedDesign(designEdited));
    }

    const {areAllAccountsSupported} = await updatePublishingDesignsMeta(designEdited);

    setIsPreviewLoading(true);

    pollForPosterPreviewGenerationStatus(
      designEdited.posterHashedId,
      (): void => {
        void updatePublishingDesignsMeta(designEdited).finally(() => {
          setIsPreviewLoading(false);
        });
      },
      (err: unknown): void => {
        console.error(err);
        setIsPreviewLoading(false);
      }
    );

    if (areAllAccountsSupported) {
      dispatch(updateTriggerNext());
      dispatch(hideAccountLevelErrorMessage());
    } else {
      dispatch(
        updateAccountLevelErrorMessage({
          title: window.i18next.t('pmwjs_unsupported_account_error_title'),
          description: window.i18next.t('pmwjs_unsupported_account_error_desc'),
        })
      );
      dispatch(updateTriggerPrevious());
    }
  };

  return {
    onClickEditDesign,
    onEmbeddedEditorOpened,
    onEmbeddedEditorForTemplateClosed,
    triggerEditDesignsFlow,
  };
}

interface UseWizardInitOpts {
  post: SocialPost | undefined;
  accounts: SocialAccount[];
  accountIdsForPost: number[];
  design: DesignSelection | undefined;
  template: DesignSelection | undefined;
  isGalleryFlow: boolean;
  isSelectTemplateFlow: boolean;
  emphasizedPlatform: SocialPlatform | undefined;
  hasSeenOnboarding: boolean;
  hasSeenConfetti: boolean;
  hasUserPublishedBefore: boolean;
  isSocialMediaWizardAiCaptionsEnabled: boolean;
  isGoogleBusinessProfileEnabled: boolean;
  numParentAccountsConnected: number;
  setDesignsToPublish: (designs: DesignSelection[]) => void;
}

export function useWizardInit({
  post,
  accounts,
  accountIdsForPost,
  design,
  template,
  isGalleryFlow,
  isSelectTemplateFlow,
  emphasizedPlatform,
  hasSeenOnboarding,
  hasSeenConfetti,
  hasUserPublishedBefore,
  isSocialMediaWizardAiCaptionsEnabled,
  isGoogleBusinessProfileEnabled,
  numParentAccountsConnected,
  setDesignsToPublish,
}: UseWizardInitOpts): void {
  const dispatch = useAppDispatch();
  const wizardStep = useWizardStep();
  const connectedAccounts = useWizardConnectedAccounts();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const captionErrors = useWizardCaptionErrors();
  const titleErrors = useWizardTitleErrors();
  const publishingAction = useSocialPostPublishingAction();

  useEffect((): void => {
    const nextStep = getNextWizardStep(post, accounts, null, {
      isGalleryFlow: isSelectTemplateFlow,
      template,
      emphasizedPlatform,
    });

    if (nextStep) {
      dispatch(updateWizardStep(nextStep));
    }

    dispatch(updateHasSeenOnboarding(hasSeenOnboarding));
    dispatch(updateHasSeenConfetti(hasSeenConfetti));
    dispatch(updateHasUserPublishedBefore(hasUserPublishedBefore));
    dispatch(updateIsSocialMediaWizardAiCaptionsEnabled(isSocialMediaWizardAiCaptionsEnabled));
    dispatch(updateIsGoogleBusinessProfileEnabled(isGoogleBusinessProfileEnabled));
    dispatch(updateNumParentAccountsConnected(numParentAccountsConnected));

    const sanitizedPlatformToConnect = getSanitizedEmphasizedPlatform(emphasizedPlatform);

    if (sanitizedPlatformToConnect) {
      dispatch(updateEmphasizedPlatform(sanitizedPlatformToConnect));
      dispatch(updateAccountTypeToConnect(sanitizedPlatformToConnect));
    }

    const captionErrorAccount = getCaptionErrorAccountId(accountIdsToPublishTo, captionErrors);
    const titleErrorAccount = getTitleErrorAccountId(accountIdsToPublishTo, titleErrors);
    const isAnyAccountErrorProne = captionErrorAccount !== null || titleErrorAccount !== null;

    if (wizardStep === SocialMediaWizardStep.PUBLISH && isAnyAccountErrorProne) {
      dispatch(updatePublishingAction(undefined));
    } else if (wizardStep === SocialMediaWizardStep.ADD_DETAILS) {
      dispatch(initPublishingParams());
      dispatch(syncPublishingParams());
      void dispatch(
        savePreferredProfileSelectionUser({
          socialMediaAccountIds: accountIdsToPublishTo,
          idPost: post?.id,
        })
      );
    }

    if (wizardStep === SocialMediaWizardStep.PUBLISH) {
      if (publishingAction === undefined && !isAnyAccountErrorProne) {
        dispatch(updatePublishingAction(SocialPostPublishingAction.PUBLISH));
      }
    } else {
      dispatch(updatePublishingAction(undefined));
    }

    if (connectedAccounts.length === 0) {
      dispatch(disableNextButton());
    } else if (accountIdsToPublishTo.length !== 0) {
      dispatch(enableNextButton());
    }

    if (accountIdsForPost.length > 0) {
      dispatch(updateAccountIdsToPublishTo(accountIdsForPost));
    }

    if (design) {
      dispatch(updateDesignsToPublish([design]));
    }

    if (isGalleryFlow) {
      dispatch(updateIsTemplateSelected(true));
    }

    if (template) {
      dispatch(updateIsTemplateSelected(true));
      dispatch(updateDesignsToPublish([template]));
      setDesignsToPublish([template]);
    }

    if (post) {
      const {publishingParams} = post;
      if (publishingParams) {
        dispatch(updatePublishingParams(publishingParams));
      }

      dispatch(updatePost(post));
    }
  }, []);
}
